define((require) => {
    "use strict";

    const Marionette                 = require("marionette");
    const TransformableWidgetFactory = require("ui/experience/widgets/TransformableWidgetFactory");
    const WidgetTypes                = require("shared/ui/experience/widgets/WidgetTypes");
    const WidgetTransformer          = require("ui/editor/WidgetTransformer");
    const {ETW_IS_READY}              = require("ui/experience/widgets/textEnhancedWidget/textWidgetEvents");
    /**
     * The container inside each scene that holds all the widgets, and instantiates their views.
     *
     * @return {Marionette CollectionView}
     */
    return Marionette.CollectionView.extend({

        className: "widget-container-inner widget-collection-view-container",

        transformer: null,

        viewModel: null,

        _layer: null,

        scene: null,

        attributes: {
            "data-dragselectable": true
        },

        initialize({layer, scene, viewScope}) {
            this.viewModel = viewScope.viewModel;
            this._layer = layer;
            this.scene = scene;

            this.transformer = new WidgetTransformer({
                container: this.$el,
                layer,
                scene,
                viewModel: this.viewModel
            });

            this.el.className += ` widget-collection-view-container-${layer.value}`;
            this.el.className += ` widget-collection-view-container-${this.scene.get("sceneNumber")}`;
            this.transformer.setViewportBoundingBoxProvider(this.viewportBoundingBoxProvider.bind(this));
            this.children.each(this.transformer.addWidget.bind(this.transformer));
            this._addListeners();
        },

        onRender() {
            this.transformer.editorRenderHandler(this.options.viewScope.viewModel.get("zoomModel").get("scale"));
        },

        viewportBoundingBoxProvider() {
            return this.options.viewScope.getViewportElement().get(0).getBoundingClientRect();
        },

        _addListeners() {
            this.on("itemview:update", this._updateControlsHandler);

            this.listenTo(this.viewModel.selection, {
                "change:widgets": this._selectedWidgetsChangeHandler
            });
            this.listenTo(this.viewModel.get("zoomModel"), {
                "change:scale": this._updateControlsHandler
            });

            this.listenTo(this.transformer, "setSelectedWidgets", this._setSelectedWidgetsHandler);

            this.listenTo(this.viewModel, "change:currentLayer change:sceneNumber", this._setSelectedWidgetsHandler);

            this.listenTo(this.viewModel.get("experience"), "change:selectedWidgetId", this._setWidgetHighlight);

            this.listenTo(this.viewModel, ETW_IS_READY, this._updateControlsHandler);
        },

        collectionEvents: {
            "change": "_updateControlsHandler",
            "destroy remove": "_destroyRemoveHandler"
        },

        _updateControlsHandler() {
            this.transformer.updateControls();
        },

        _destroyRemoveHandler() {
            this.transformer.hideControls();
        },

        _setSelectedWidgetsHandler(selectedWidgets) {
            // If selected widgets were updated by our instance of the WidgetTransformer,
            // make sure that the newly selected widgets are all in the current layer. If any of
            // them are not, abort and clear the selection. If yes, ask our instance of the
            // WidgetSelectionViewModel to add the newly selected widgets to its list.
            const layerUuids = [];
            let selectedUuids = [];
            const widgetsAreSelected = selectedWidgets && selectedWidgets.length > 0;

            // Get an array of the uuid's of all widgets in the current layer.
            this.collection.each((widget) => {
                layerUuids.push(widget.get("uuid"));
                if (widget.get("group")) {
                    widget.get("group").models.map((model) => layerUuids.push(model.get("uuid")));
                }
            });

            // Get an array of the uuid's of the widgets passed to us by the WidgetTransformer.
            if (widgetsAreSelected) {
                selectedUuids = selectedWidgets.map((widget) => widget.model.get("uuid"));
            }

            // If all of the selected widgets are in the current layer, we're good to go and
            // can ask our instance of the WidgetSelectionViewModel to add these widgets.
            // If one or more of the selected widgets isn't in this layer, clear the selection.
            if (this._includesAll(selectedUuids, layerUuids) && widgetsAreSelected) {
                this.viewModel.selection.select(selectedWidgets);
            } else {
                this.viewModel.selection.clear();
                this.transformer.hideControls();
            }
        },

        _includesAll(subArray, array) {
            if (subArray && subArray.length > 0) {
                subArray.forEach((element) => {
                    if (!array.includes(element)) {
                        return false;
                    }
                });
            }
            return true;
        },

        _selectedWidgetsChangeHandler() {
            const selectedViews = this.viewModel.selection.get("widgets");
            const isCurrentScene = (this.scene === this.viewModel.get("scene"));

            if (isCurrentScene) {
                this.transformer.selectWidget(selectedViews);
            }

            this.children.each((childView) => {
                // Triggers deselect from here is more consistent, previously we trigger it from Freetransform
                // See bug MOS-1857
                childView.trigger("deselect");

                if (childView.model.get("group") && childView.groupCollectionView) {
                    childView.groupCollectionView.children.each((grandChildView) => {
                        grandChildView.trigger("deselect");
                        const grandChildViewIsSelected = selectedViews.indexOf(grandChildView) > -1;
                        grandChildView.$el.toggleClass(
                            "widget-selected",
                            grandChildViewIsSelected && isCurrentScene
                        );
                    });
                }

                const childViewIsSelected = selectedViews.indexOf(childView) > -1;
                childView.$el.toggleClass("widget-selected", childViewIsSelected && isCurrentScene);
            });

            this.$el.toggleClass("multi-selection", this.viewModel.selection.isMultiple());
        },

        _setWidgetHighlight() {
            if (this.viewModel.get("experience").get("selectedWidgetId")) {
                this.$el.addClass("has-highlight");
            } else {
                this.$el.removeClass("has-highlight");
            }
        },

        setActiveState() {
            const isActive = (this.viewModel.get("currentLayer") === this._layer);

            this.$el.toggleClass("active-layer-widgets", isActive);
        },

        /**
         * Get the view for each widget, this overwrites the default from marionette
         *
         * @param {Backbone.Model} item
         * @returns {AbstractWidgetView}    Returns the view defined for the model
         */
        getItemView(item) {
            const type = item.get("type");
            const ViewClass = WidgetTypes.lookup(type).viewClass;

            return TransformableWidgetFactory.create(type, ViewClass);
        },

        itemViewOptions() {
            return {
                viewModel: this.viewModel,
                edited: this.options.edited,
                container: this.$el,
                TransformableWidgetFactory,
                addChildWidgetToTransformer: (childWidgetView, parentTransform) => {
                    this.transformer.addWidget(childWidgetView, parentTransform);
                },
                removeChildWidgetFromTransformer: (childWidgetView) => {
                    this.transformer.removeWidget(childWidgetView);
                },
                updateControls: () => {
                    this._updateControlsHandler();
                },
                parentCollectionView: this
            };
        },

        /**
         * Marionette fires this when an itemview has been built.
         *
         * @param {Object} itemView
         */
        onAfterItemAdded(itemView) {
            this.transformer.addWidget(itemView);
        },

        onClose() {
            this.transformer.removeTransformer();
            this.transformer.close();
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/WidgetCollectionView.js